import AddTaskIcon from '@mui/icons-material/AddTask';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Checkbox, IconButton, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import type { FC } from 'react';

import CircleLoader from 'components/CircleLoader/CircleLoader';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import { StatusIdE } from 'declarations/enums/tickets';
import type { AttachmentI } from 'declarations/interfaces/attachments';
import type { NoopT } from 'declarations/types/common';

import ApproveAttachmentsPopup from './components/ApproveAttachmentsPopup/ApproveAttachmentsPopup';
import DeleteAttachmentPopup from './components/DeleteAttachmentPopup/DeleteAttachmentPopup';
import { ActionTypeE } from './declarations';
import useViewAttachmentsPopup from './hooks/useViewAttachmentsPopup';
import {
  AttachmentNameStyled,
  FlexStyled,
  LabelStyled,
  ListItemActionsRowStyled,
  ListItemMetaFieldStyled,
  ListItemMetaFieldTypeStyled,
  ListItemMetaFieldsStyled,
  ListItemStyled,
  ListItemTopRowStyled,
  ListStyled,
  ValueStyled,
} from './ViewAttachmentsPopup.styled';

export type ViewAttachmentsCardPropsT = {
  ticketId: string;
  ticketStatus: StatusIdE;
  isExpanded: boolean;
  /** When provided, parent owns attachments state (e.g. for approve button in header). */
  attachments?: AttachmentI[] | null;
  isLoadingAttachments?: boolean;
  selectedAttachmentsForApprove?: string[] | null;
  handleApproveAttachClick?: (attachmentName: string) => NoopT;
  handleOpenDeleteAttachmentPopup?: (attachment: AttachmentI) => NoopT;
  handleCloseDeleteAttachmentPopup?: NoopT;
  selectedAttachment?: AttachmentI | null;
  handleAttachmentClick?: (attachment: AttachmentI, actionType: ActionTypeE) => NoopT;
  isLoadingAttachmentForPreview?: boolean;
  isLoadingAttachmentForDownload?: boolean;
  isAttachmentForDownloadError?: boolean;
  isAttachmentForPreviewError?: boolean;
  isDeleteAttachmentPopupOpened?: boolean;
  isApproveAttachmentsPopupOpened?: boolean;
  handleOpenApproveAttachmentsPopup?: () => void;
  handleCloseApproveAttachmentsPopup?: () => void;
};

const ViewAttachmentsCard: FC<ViewAttachmentsCardPropsT> = ({
  ticketId,
  ticketStatus,
  isExpanded: _isExpanded,
  attachments: attachmentsProp,
  isLoadingAttachments: isLoadingAttachmentsProp,
  selectedAttachmentsForApprove: selectedAttachmentsForApproveProp,
  handleApproveAttachClick: handleApproveAttachClickProp,
  handleOpenDeleteAttachmentPopup: handleOpenDeleteAttachmentPopupProp,
  handleCloseDeleteAttachmentPopup: handleCloseDeleteAttachmentPopupProp,
  selectedAttachment: selectedAttachmentProp,
  handleAttachmentClick: handleAttachmentClickProp,
  isLoadingAttachmentForPreview: isLoadingAttachmentForPreviewProp,
  isLoadingAttachmentForDownload: isLoadingAttachmentForDownloadProp,
  isAttachmentForDownloadError: isAttachmentForDownloadErrorProp,
  isAttachmentForPreviewError: isAttachmentForPreviewErrorProp,
  isDeleteAttachmentPopupOpened: isDeleteAttachmentPopupOpenedProp,
  isApproveAttachmentsPopupOpened: isApproveAttachmentsPopupOpenedProp,
  handleOpenApproveAttachmentsPopup: handleOpenApproveAttachmentsPopupProp,
  handleCloseApproveAttachmentsPopup: handleCloseApproveAttachmentsPopupProp,
}) => {
  const hookResult = useViewAttachmentsPopup({ ticketId });
  const useParentState = attachmentsProp !== undefined;
  const attachments = useParentState ? attachmentsProp ?? null : hookResult.attachments;
  const isLoadingAttachments = useParentState
    ? isLoadingAttachmentsProp ?? false
    : hookResult.isLoadingAttachments;
  const selectedAttachmentsForApprove = useParentState
    ? selectedAttachmentsForApproveProp
    : hookResult.selectedAttachmentsForApprove;
  const handleApproveAttachClick =
    (useParentState ? handleApproveAttachClickProp : hookResult.handleApproveAttachClick) ??
    hookResult.handleApproveAttachClick;
  const handleOpenDeleteAttachmentPopup =
    (useParentState
      ? handleOpenDeleteAttachmentPopupProp
      : hookResult.handleOpenDeleteAttachmentPopup) ?? hookResult.handleOpenDeleteAttachmentPopup;
  const handleCloseDeleteAttachmentPopup =
    (useParentState
      ? handleCloseDeleteAttachmentPopupProp
      : hookResult.handleCloseDeleteAttachmentPopup) ?? hookResult.handleCloseDeleteAttachmentPopup;
  const selectedAttachment = useParentState
    ? selectedAttachmentProp
    : hookResult.selectedAttachment;
  const handleAttachmentClick =
    (useParentState ? handleAttachmentClickProp : hookResult.handleAttachmentClick) ??
    hookResult.handleAttachmentClick;
  const isLoadingAttachmentForPreview = useParentState
    ? isLoadingAttachmentForPreviewProp ?? false
    : hookResult.isLoadingAttachmentForPreview;
  const isLoadingAttachmentForDownload = useParentState
    ? isLoadingAttachmentForDownloadProp ?? false
    : hookResult.isLoadingAttachmentForDownload;
  const isAttachmentForDownloadError = useParentState
    ? isAttachmentForDownloadErrorProp ?? false
    : hookResult.isAttachmentForDownloadError;
  const isAttachmentForPreviewError = useParentState
    ? isAttachmentForPreviewErrorProp ?? false
    : hookResult.isAttachmentForPreviewError;
  const isDeleteAttachmentPopupOpened = useParentState
    ? isDeleteAttachmentPopupOpenedProp ?? false
    : hookResult.isDeleteAttachmentPopupOpened;
  const isApproveAttachmentsPopupOpened = useParentState
    ? isApproveAttachmentsPopupOpenedProp ?? false
    : hookResult.isApproveAttachmentsPopupOpened;
  const handleOpenApproveAttachmentsPopup = useParentState
    ? handleOpenApproveAttachmentsPopupProp ?? hookResult.handleOpenApproveAttachmentsPopup
    : hookResult.handleOpenApproveAttachmentsPopup;
  const handleCloseApproveAttachmentsPopup = useParentState
    ? handleCloseApproveAttachmentsPopupProp ?? hookResult.handleCloseApproveAttachmentsPopup
    : hookResult.handleCloseApproveAttachmentsPopup;

  const isAttachmentsFetchingError = useParentState ? false : hookResult.isAttachmentsFetchingError;

  return (
    <>
      {!!attachments?.length && !isLoadingAttachments && (
        <FlexContainer direction="column" align="stretch">
          <ListStyled>
            {attachments?.map((attachment, i) => {
              const isPreviewActive =
                isLoadingAttachmentForPreview &&
                selectedAttachment?.fileName === attachment.fileName;
              const isDownloadActive =
                isLoadingAttachmentForDownload &&
                selectedAttachment?.fileName === attachment.fileName;
              const isDeleteBtnDisabled = ticketStatus !== StatusIdE.Open;
              const isSelected =
                selectedAttachmentsForApprove?.includes(attachment.fileName) &&
                !attachment.approvedForClaimSubmission;
              return (
                <ListItemStyled key={`attachment-${i}`} isSelected={isSelected}>
                  <ListItemTopRowStyled>
                    <Tooltip
                      title={
                        attachment.approvedForClaimSubmission
                          ? 'Attachment is approved'
                          : 'Attachment is not approved'
                      }
                      arrow
                    >
                      <Checkbox
                        checked={
                          attachment.approvedForClaimSubmission ||
                          (selectedAttachmentsForApprove?.includes(attachment.fileName) ?? false)
                        }
                        disabled={attachment.approvedForClaimSubmission}
                        onChange={(): void => handleApproveAttachClick(attachment.fileName)()}
                        size="small"
                        color="primary"
                      />
                    </Tooltip>
                    <AttachmentNameStyled>
                      {attachment.originalName !== undefined
                        ? attachment.originalName
                        : attachment.fileName}
                    </AttachmentNameStyled>
                  </ListItemTopRowStyled>
                  <ListItemActionsRowStyled>
                    <Tooltip title="View" arrow>
                      <IconButton
                        aria-label="View"
                        disabled={isPreviewActive}
                        onClick={handleAttachmentClick(attachment, ActionTypeE.View)}
                        sx={{ color: (t): string => t.palette.primary.main }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download" arrow>
                      <IconButton
                        aria-label="Download"
                        disabled={isDownloadActive}
                        onClick={handleAttachmentClick(attachment, ActionTypeE.Download)}
                        sx={{ color: (t): string => t.palette.primary.main }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        aria-label="Delete"
                        disabled={isDeleteBtnDisabled}
                        onClick={handleOpenDeleteAttachmentPopup(attachment)}
                        sx={{ color: (t): string => t.palette.primary.main }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <ListItemMetaFieldsStyled>
                      <ListItemMetaFieldTypeStyled>
                        <LabelStyled size="xs">Type</LabelStyled>
                        <Tooltip title={attachment.attachmentTypeCode || ''} arrow>
                          <span>
                            <ValueStyled color="primaryText" size="xs">
                              {attachment.attachmentTypeCode}
                            </ValueStyled>
                          </span>
                        </Tooltip>
                      </ListItemMetaFieldTypeStyled>
                      <ListItemMetaFieldStyled>
                        <LabelStyled size="xs">Uploaded</LabelStyled>
                        <ValueStyled color="primaryText" size="xs">
                          {format(new Date(attachment.createdDateTime), 'MMM d, yyyy')}
                        </ValueStyled>
                      </ListItemMetaFieldStyled>
                    </ListItemMetaFieldsStyled>
                  </ListItemActionsRowStyled>
                </ListItemStyled>
              );
            })}
          </ListStyled>
          {!useParentState && (
            <FlexContainer justify="flex-end">
              <Tooltip title="Approve selected" arrow>
                <span>
                  <IconButton
                    aria-label="Approve selected"
                    disabled={
                      !selectedAttachmentsForApprove?.length ||
                      selectedAttachmentsForApprove?.length === 0
                    }
                    onClick={handleOpenApproveAttachmentsPopup}
                    sx={{ color: (t): string => t.palette.primary.main }}
                  >
                    <AddTaskIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </FlexContainer>
          )}
        </FlexContainer>
      )}

      {(isAttachmentsFetchingError ||
        isAttachmentForDownloadError ||
        isAttachmentForPreviewError) && (
        <FlexContainer direction="column">
          <ErrorSection />
        </FlexContainer>
      )}

      {isLoadingAttachments && (
        <FlexStyled align="center" justify="center">
          <CircleLoader />
        </FlexStyled>
      )}

      {!useParentState && (
        <>
          <DeleteAttachmentPopup
            ticketId={ticketId}
            fileName={selectedAttachment?.fileName || ''}
            originalName={
              (selectedAttachment?.originalName !== undefined
                ? selectedAttachment?.originalName
                : selectedAttachment?.fileName) || ''
            }
            isOpen={isDeleteAttachmentPopupOpened}
            onClose={handleCloseDeleteAttachmentPopup}
          />
          <ApproveAttachmentsPopup
            ticketId={ticketId}
            fileNames={selectedAttachmentsForApprove || []}
            isOpen={isApproveAttachmentsPopupOpened}
            onClose={handleCloseApproveAttachmentsPopup}
          />
        </>
      )}
    </>
  );
};

export default ViewAttachmentsCard;
